import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import $ from 'jquery';
import 'datatables.net-dt/js/dataTables.dataTables';
import './CustomDataTableStyles.css';
import './EnhancedStyles.css';
import { Link, useNavigate } from 'react-router-dom';
import PlotMap from './PlotMap';
import PlotMapHardstone from './PlotMapHardstone';

// Import icons from lucide-react
import { 
  Home,
  Building2,
  TrendingUp,
  MapPin,
  Search,
  Eye,
  EyeOff,
  Lock,
  Mail,
  User,
  Phone,
  MessageCircle,
  Instagram,
  Facebook,
  Twitter,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  ChevronLeft,
  ArrowRight,
  Users,
  Award,
  Map,
  Shield,
  Key,
  LogIn,
  X,
  MessageSquare,
  Globe,
  Heart,
  Star,
  CheckCircle,
  Building,
  MapPinned,
  Landmark,
  ChartBar,
  Briefcase,
  DollarSign,
  Navigation,
  PhoneCall,
  Send,
  Menu,
  Calendar,
  Clock,
  ClipboardCheck,
  BadgeCheck,
  Target,
  Zap,
  Sparkles,
  Filter,
  Download,
  Share2,
  Bookmark,
  Play,
  Pause,
  Volume2,
  Video,
  Image,
  Camera,
  Maximize2,
  Minimize2,
  RefreshCw,
  Settings,
  HelpCircle,
  Info,
  AlertCircle,
  ThumbsUp,
  ZoomIn,
  ZoomOut,
  Layers,
  Compass,
  Navigation2,
  CreditCard,
  Calculator,
  Percent,
  CalendarDays,
  FileText,
  BarChart3,
  PieChart,
  LineChart,
  Sun,
  Moon,
  CloudSun,
  Eye as EyeIcon
} from 'lucide-react';

const HomePage = () => {
  let navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [plotsRidgehill, setPlotsRidgehill] = useState([]);
  const [plotsHardstone, setPlotsHardstone] = useState([]);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [entriesPerPageHardstone, setEntriesPerPageHardstone] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('home');
  const [isScrolled, setIsScrolled] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedPlot, setSelectedPlot] = useState(null);
  const [showPlotModal, setShowPlotModal] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [sortBy, setSortBy] = useState('default');
  const [theme, setTheme] = useState('normal'); // normal, day, night
  const [particles, setParticles] = useState([]);
  const [isPlaying, setIsPlaying] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showVirtualTour, setShowVirtualTour] = useState(false);
  const [notification, setNotification] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [compareList, setCompareList] = useState([]);
  const [showCompareModal, setShowCompareModal] = useState(false);

  const server_app_api_php = window.SERVER_APP_API_PHP;
  const audioRef = useRef(null);
  const carouselIntervalRef = useRef(null);

  // Initialize theme from localStorage
  useEffect(() => {
    const savedTheme = localStorage.getItem('homepage-theme');
    if (savedTheme && ['normal', 'day', 'night'].includes(savedTheme)) {
      setTheme(savedTheme);
      applyTheme(savedTheme);
    } else {
      // Default to normal
      setTheme('normal');
      applyTheme('normal');
    }
  }, []);

  // Apply theme to the document body
  const applyTheme = (newTheme) => {
    // Remove all theme classes first
    document.body.classList.remove('home-page-normal', 'home-page-day', 'home-page-night', 'dark');
    
    // Apply the selected theme class
    if (newTheme === 'normal') {
      document.body.classList.add('home-page-normal');
    } else if (newTheme === 'day') {
      document.body.classList.add('home-page-day');
    } else if (newTheme === 'night') {
      document.body.classList.add('home-page-night', 'dark');
    }
  };

  // Theme toggle function
  const toggleTheme = () => {
    const themes = ['normal', 'day', 'night'];
    const currentIndex = themes.indexOf(theme);
    const nextIndex = (currentIndex + 1) % themes.length;
    const newTheme = themes[nextIndex];
    
    setTheme(newTheme);
    applyTheme(newTheme);
    localStorage.setItem('homepage-theme', newTheme);
    
    showNotification(`Switched to ${newTheme} mode`, 'success');
  };

  // Get theme icon and label
  const getThemeInfo = () => {
    switch(theme) {
      case 'normal':
        return { icon: EyeIcon, label: 'Normal', color: 'from-blue-500 to-purple-500', textColor: 'text-white' };
      case 'day':
        return { icon: Sun, label: 'Day', color: 'from-yellow-400 to-orange-500', textColor: 'text-gray-900' };
      case 'night':
        return { icon: Moon, label: 'Night', color: 'from-gray-800 to-blue-900', textColor: 'text-white' };
      default:
        return { icon: EyeIcon, label: 'Normal', color: 'from-blue-500 to-purple-500', textColor: 'text-white' };
    }
  };

  // Initialize particles
  useEffect(() => {
    const newParticles = [];
    for (let i = 0; i < 20; i++) {
      newParticles.push({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 4 + 1,
        duration: Math.random() * 20 + 10,
        delay: Math.random() * 5
      });
    }
    setParticles(newParticles);
  }, []);

  // Enhanced scroll handling
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      if (window.scrollY > 300) {
        setShowStats(true);
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Carousel auto-play functionality
  useEffect(() => {
    if (isPlaying) {
      carouselIntervalRef.current = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
      }, 5000);
    } else {
      clearInterval(carouselIntervalRef.current);
    }

    return () => {
      clearInterval(carouselIntervalRef.current);
    };
  }, [isPlaying]);

  // Notification system
  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // Favorites functionality
  const toggleFavorite = (plot) => {
    const isFavorite = favorites.some(fav => fav.id === plot.id);
    if (isFavorite) {
      setFavorites(favorites.filter(fav => fav.id !== plot.id));
      showNotification('Removed from favorites', 'info');
    } else {
      setFavorites([...favorites, plot]);
      showNotification('Added to favorites', 'success');
    }
  };

  // Comparison functionality
  const toggleCompare = (plot) => {
    if (compareList.some(item => item.id === plot.id)) {
      setCompareList(compareList.filter(item => item.id !== plot.id));
    } else if (compareList.length < 4) {
      setCompareList([...compareList, plot]);
      showNotification('Added to comparison', 'success');
    } else {
      showNotification('Maximum 4 properties can be compared', 'warning');
    }
  };

  // Mortgage calculator
  const calculateMortgage = (price, downPayment = 20, years = 20, rate = 7.5) => {
    const principal = price * (1 - downPayment / 100);
    const monthlyRate = rate / 100 / 12;
    const months = years * 12;
    const monthlyPayment = principal * monthlyRate * Math.pow(1 + monthlyRate, months) / 
                          (Math.pow(1 + monthlyRate, months) - 1);
    
    return {
      monthly: Math.round(monthlyPayment),
      total: Math.round(monthlyPayment * months),
      downPayment: Math.round(price * (downPayment / 100))
    };
  };

  // Virtual tour function
  const startVirtualTour = () => {
    setShowVirtualTour(true);
    showNotification('Starting virtual tour...', 'info');
    
    setTimeout(() => {
      showNotification('Virtual tour loaded! Use arrow keys to navigate', 'success');
    }, 1000);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Enhanced login with validation
  const handleLogin = async (event) => {
    event.preventDefault();
    if (username.trim() !== '' && password.trim() !== '') {
      setLoading(true);
      
      showNotification('Authenticating...', 'info');
      
      const formData = new FormData();
      formData.append('apiType', 'login');
      formData.append('username', username.trim());
      formData.append('password', password.trim());

      try {
        const response = await axios.post(`${server_app_api_php}/server/index.php`, formData);
        const data = response.data;
        
        if (response.data.status === "LoggedIn") {
          const sessionData = data.sessionData;
          localStorage.setItem('userName', sessionData.userName);
          localStorage.setItem('userID', sessionData.userID);
          localStorage.setItem('companyID', sessionData.companyID);
          localStorage.setItem('businessLocationID', sessionData.businessLocationID);
          localStorage.setItem('userPosition', sessionData.userPosition);
          localStorage.setItem('userRole', sessionData.userRole);
          localStorage.setItem('userType', sessionData.userType);
          localStorage.setItem('email', sessionData.email);
          localStorage.setItem('business_locationIDMain', "");
          
          setUsername('');
          setPassword('');
          
          showNotification('Login successful! Redirecting...', 'success');
          
          setTimeout(() => {
            window.location.href = "Dashboard";
          }, 1500);
        } else {
          showNotification('Invalid credentials. Please try again.', 'error');
        }
      } catch (error) {
        console.log("Connection Error = ", error);
        showNotification('Connection error. Please check your internet.', 'error');
      } finally {
        setLoading(false);
      }
    } else {
      showNotification('Please fill all fields.', 'warning');
    }
  };

  // Fetch plots with enhanced error handling
  useEffect(() => {
    const fetchDataRidgehill = async () => {
      const formData = new FormData();
      formData.append('apiType', 'fetchUnsoldRidgehillPlots');
      try {
        const response = await axios.post(`${server_app_api_php}/server/index.php`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        const plotsWithId = response.data.map((plot, index) => ({
          ...plot,
          id: `ridgehill-${index}`,
          favorite: false,
          inComparison: false,
          calculatedMortgage: calculateMortgage(
            parseFloat(plot.plotPricesNew?.replace(/[^0-9.-]+/g, "") || 0)
          )
        }));
        
        setPlotsRidgehill(plotsWithId);
        
        setTimeout(() => {
          initializeDataTable('#ridgehillPlots');
        }, 1000);
      } catch (error) {
        console.error('Error fetching Ridgehill plots:', error);
        showNotification('Error loading Ridgehill plots', 'error');
      }
    };
    fetchDataRidgehill();
  }, []);

  useEffect(() => {
    const fetchDataHardstone = async () => {
      const formData = new FormData();
      formData.append('apiType', 'fetchUnsoldHardstonePlots');
      try {
        const response = await axios.post(`${server_app_api_php}/server/index.php`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        const plotsWithId = response.data.map((plot, index) => ({
          ...plot,
          id: `hardstone-${index}`,
          favorite: false,
          inComparison: false,
          calculatedMortgage: calculateMortgage(
            parseFloat(plot.plotPricesNew?.replace(/[^0-9.-]+/g, "") || 0)
          )
        }));
        
        setPlotsHardstone(plotsWithId);
        
        setTimeout(() => {
          initializeDataTable('#hardstonePlots');
        }, 1000);
      } catch (error) {
        console.error('Error fetching Hardstone plots:', error);
        showNotification('Error loading Hardstone plots', 'error');
      }
    };
    fetchDataHardstone();
  }, []);

  // Enhanced DataTable initialization
  const initializeDataTable = (selector) => {
    if ($.fn.DataTable.isDataTable(selector)) {
      $(selector).DataTable().destroy();
    }
    
    $(selector).DataTable({
      pagingType: "full_numbers",
      paging: true,
      searching: true,
      info: true,
      lengthChange: false,
      language: {
        paginate: {
          first: '«',
          last: '»',
          next: '›',
          previous: '‹'
        },
        search: "_INPUT_",
        searchPlaceholder: "Search plots..."
      },
      dom: '<"top"lf>rt<"bottom"ip><"clear">',
      initComplete: function() {
        this.api().columns().every(function() {
          const column = this;
          const header = $(column.header());
          header.addClass('select-header');
        });
      }
    });
  };

  // Enhanced search with filters
  const handleSearch = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    const ridgehillTable = $('#ridgehillPlots').DataTable();
    const hardstoneTable = $('#hardstonePlots').DataTable();
    ridgehillTable.search(query).draw();
    hardstoneTable.search(query).draw();
  };

  const handleEntriesPerPage = (event) => {
    const value = parseInt(event.target.value, 10);
    if (value === 1) {
      $('#ridgehillPlots').DataTable().page.len(-1).draw();
    } else {
      setEntriesPerPage(value);
      $('#ridgehillPlots').DataTable().page.len(value).draw();
    }
  };

  const handleEntriesPerPageHardstone = (event) => {
    const value = parseInt(event.target.value, 10);
    if (value === 1) {
      $('#hardstonePlots').DataTable().page.len(-1).draw();
    } else {
      setEntriesPerPageHardstone(value);
      $('#hardstonePlots').DataTable().page.len(value).draw();
    }
  };

  // Apply filters
  const applyFilter = (filterType) => {
    setActiveFilter(filterType);
    
    let filteredRidgehill = plotsRidgehill;
    let filteredHardstone = plotsHardstone;
    
    switch(filterType) {
      case 'affordable':
        filteredRidgehill = plotsRidgehill.filter(plot => 
          parseFloat(plot.plotPricesNew?.replace(/[^0-9.-]+/g, "") || 0) < 50000
        );
        filteredHardstone = plotsHardstone.filter(plot => 
          parseFloat(plot.plotPricesNew?.replace(/[^0-9.-]+/g, "") || 0) < 50000
        );
        break;
      case 'premium':
        filteredRidgehill = plotsRidgehill.filter(plot => 
          parseFloat(plot.plotPricesNew?.replace(/[^0-9.-]+/g, "") || 0) > 100000
        );
        filteredHardstone = plotsHardstone.filter(plot => 
          parseFloat(plot.plotPricesNew?.replace(/[^0-9.-]+/g, "") || 0) > 100000
        );
        break;
      case 'large':
        filteredRidgehill = plotsRidgehill.filter(plot => 
          parseFloat(plot.plotAreasNew?.replace(/[^0-9.-]+/g, "") || 0) > 1000
        );
        filteredHardstone = plotsHardstone.filter(plot => 
          parseFloat(plot.plotAreasNew?.replace(/[^0-9.-]+/g, "") || 0) > 1000
        );
        break;
    }
  };

  // Sort plots
  const handleSort = (sortType) => {
    setSortBy(sortType);
    
    const sortedRidgehill = [...plotsRidgehill];
    const sortedHardstone = [...plotsHardstone];
    
    switch(sortType) {
      case 'price-low':
        sortedRidgehill.sort((a, b) => 
          parseFloat(a.plotPricesNew?.replace(/[^0-9.-]+/g, "") || 0) - 
          parseFloat(b.plotPricesNew?.replace(/[^0-9.-]+/g, "") || 0)
        );
        sortedHardstone.sort((a, b) => 
          parseFloat(a.plotPricesNew?.replace(/[^0-9.-]+/g, "") || 0) - 
          parseFloat(b.plotPricesNew?.replace(/[^0-9.-]+/g, "") || 0)
        );
        break;
      case 'price-high':
        sortedRidgehill.sort((a, b) => 
          parseFloat(b.plotPricesNew?.replace(/[^0-9.-]+/g, "") || 0) - 
          parseFloat(a.plotPricesNew?.replace(/[^0-9.-]+/g, "") || 0)
        );
        sortedHardstone.sort((a, b) => 
          parseFloat(b.plotPricesNew?.replace(/[^0-9.-]+/g, "") || 0) - 
          parseFloat(a.plotPricesNew?.replace(/[^0-9.-]+/g, "") || 0)
        );
        break;
      case 'size':
        sortedRidgehill.sort((a, b) => 
          parseFloat(b.plotAreasNew?.replace(/[^0-9.-]+/g, "") || 0) - 
          parseFloat(a.plotAreasNew?.replace(/[^0-9.-]+/g, "") || 0)
        );
        sortedHardstone.sort((a, b) => 
          parseFloat(b.plotAreasNew?.replace(/[^0-9.-]+/g, "") || 0) - 
          parseFloat(a.plotAreasNew?.replace(/[^0-9.-]+/g, "") || 0)
        );
        break;
    }
    
    setPlotsRidgehill(sortedRidgehill);
    setPlotsHardstone(sortedHardstone);
  };

  // View plot details
  const viewPlotDetails = (plot) => {
    setSelectedPlot(plot);
    setShowPlotModal(true);
  };

  // Share plot
  const sharePlot = (plot) => {
    if (navigator.share) {
      navigator.share({
        title: `Check out ${plot.plotNumberNew} at Hardstone Properties`,
        text: `Amazing property available: ${plot.plotNumberNew}`,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      showNotification('Link copied to clipboard!', 'success');
    }
  };

  // Carousel navigation functions
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    if (isPlaying) {
      clearInterval(carouselIntervalRef.current);
      carouselIntervalRef.current = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
      }, 5000);
    }
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
    if (isPlaying) {
      clearInterval(carouselIntervalRef.current);
      carouselIntervalRef.current = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
      }, 5000);
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
    if (isPlaying) {
      clearInterval(carouselIntervalRef.current);
      carouselIntervalRef.current = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
      }, 5000);
    }
  };

  // Stats data with icons
  const statsData = [
    { 
      label: 'Plots Available', 
      value: plotsRidgehill.length + plotsHardstone.length+ '+', 
      icon: MapPin, 
      color: 'from-blue-500 to-blue-600',
      trend: '',
      description: 'Available properties'
    },
    { 
      label: 'Happy Clients', 
      value: '200+', 
      icon: Users, 
      color: 'from-purple-500 to-purple-600',
      trend: '',
      description: 'Satisfied customers'
    },
    { 
      label: 'Properties Sold', 
      value: '100+', 
      icon: Building2, 
      color: 'from-emerald-500 to-emerald-600',
      trend: '',
      description: 'Successful transactions'
    },
    { 
      label: 'Experience', 
      value: 'Vast', 
      icon: Award, 
      color: 'from-amber-500 to-amber-600',
      trend: '',
      description: 'Industry expertise'
    }
  ];

  // Features with enhanced descriptions
  const features = [
    {
      title: 'Premium Residential',
      description: 'Luxury homes with modern amenities and 24/7 security',
      icon: Home,
      color: 'from-blue-500 to-cyan-500',
      items: ['24/7 Security', 'Swimming Pool', 'Gym Facility', 'Park', 'Playground']
    },
    {
      title: 'Commercial Spaces',
      description: 'Strategic locations for maximum business growth potential',
      icon: Briefcase,
      color: 'from-purple-500 to-pink-500',
      items: ['Prime Locations', 'High ROI', 'Easy Access']
    },
    {
      title: 'Investment Portfolio',
      description: 'High-yield real estate investments with guaranteed returns',
      icon: TrendingUp,
      color: 'from-green-500 to-emerald-500',
      items: ['Guaranteed Returns', 'Portfolio Management', 'Regular Updates']
    }
  ];

  // Carousel slides with background images from original code
  const heroSlides = [
    {
      title: 'Find Your Dream Home',
      subtitle: 'Explore our wide range of properties',
      description: 'Discover the perfect property that matches your lifestyle and aspirations',
      backgroundImage: "url('images/hero_bg_3.jpg')",
      buttonText: '',
      icon: Home
    },
    {
      title: 'Invest in Real Estate',
      subtitle: 'Discover investment opportunities',
      description: 'Build wealth through strategic real estate investments with proven returns',
      backgroundImage: "url('images/hero_bg_2.jpg')",
      buttonText: '',
      icon: TrendingUp
    },
    {
      title: 'Luxury Living Spaces',
      subtitle: 'Explore luxurious living options',
      description: 'Experience premium living in Zambia\'s most desirable locations',
      backgroundImage: "url('images/hero_bg_1.jpg')",
      buttonText: '',
      icon: MapPin
    }
  ];

  // Style for text container (from original code)
  const textContainerStyle = {
    position: 'relative',
    zIndex: 1,
    color: '#fff',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    padding: '40px',
    borderRadius: '20px',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  };

  const themeInfo = getThemeInfo();

  return (
    <main className={`min-h-screen overflow-hidden transition-colors duration-500 theme-transition`}>
      {/* Notification System */}
      {notification && (
        <div className={`fixed top-4 right-4 z-[9999] p-4 rounded-2xl shadow-2xl transform transition-all duration-500 ${
          notification.type === 'success' ? 'bg-emerald-500 text-white' :
          notification.type === 'error' ? 'bg-red-500 text-white' :
          notification.type === 'warning' ? 'bg-amber-500 text-white' :
          'bg-blue-500 text-white'
        }`}>
          <div className="flex items-center space-x-3">
            {notification.type === 'success' && <CheckCircle className="w-5 h-5" />}
            {notification.type === 'error' && <AlertCircle className="w-5 h-5" />}
            {notification.type === 'warning' && <AlertCircle className="w-5 h-5" />}
            <span className="font-semibold">{notification.message}</span>
            <button onClick={() => setNotification(null)} className="ml-4">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Floating Action Buttons */}
      <div className="fixed bottom-8 right-8 z-50 flex flex-col space-y-4">
        {/* Enhanced Theme Toggle Button */}
        <button 
          onClick={toggleTheme}
          className={`p-4 rounded-2xl shadow-2xl hover:shadow-3xl transform hover:scale-110 transition-all duration-300 group relative bg-gradient-to-br ${themeInfo.color} ${themeInfo.textColor}`}
        >
          <div className="relative">
            <themeInfo.icon className="w-6 h-6" />
            <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
              {themeInfo.label} Mode
            </span>
            <span className="absolute -top-2 -right-2 bg-white text-gray-900 text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
              {theme === 'normal' ? 'N' : theme === 'day' ? 'D' : 'N'}
            </span>
          </div>
        </button>
        
        {compareList.length > 0 && (
          <button 
            onClick={() => setShowCompareModal(true)}
            className="p-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-2xl shadow-2xl hover:shadow-3xl transform hover:scale-110 transition-all duration-300 group relative"
          >
            <BarChart3 className="w-6 h-6" />
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center">
              {compareList.length}
            </span>
          </button>
        )}
        
        <button 
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="p-4 bg-gradient-to-r from-emerald-500 to-green-500 text-white rounded-2xl shadow-2xl hover:shadow-3xl transform hover:scale-110 transition-all duration-300"
        >
          <ChevronUp className="w-6 h-6" />
        </button>
      </div>

      {/* Navigation Bar - Enhanced */}
      <nav className={`fixed w-full z-50 transition-all duration-500 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-2xl py-3' : 'bg-transparent py-5'
      } border-b border-gray-100/20`}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Enhanced Logo */}
            <div className="flex items-center space-x-3 group cursor-pointer">
              <div className="relative">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-500 rounded-2xl flex items-center justify-center transform group-hover:rotate-12 transition-transform duration-300 shadow-lg animate-border-glow">
                  <Home className="text-white w-6 h-6" />
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-amber-400 rounded-full border-2 border-white animate-pulse"></div>
                </div>
              </div>
              <div>
                <h1 className={`text-2xl font-bold ${isScrolled ? 'text-gray-800' : 'text-white'} leading-tight`}>
                  Hardstone<span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Properties</span>
                </h1>
                <p className={`text-xs ${isScrolled ? 'text-gray-500' : 'text-gray-200'}`}>Premium Real Estate Solutions</p>
              </div>
            </div>

            {/* Desktop Navigation - Enhanced */}
            <div className="hidden lg:flex items-center space-x-1">
              {['Home', 'Properties', 'Invest', 'About', 'Contact'].map((item, index) => (
                <a
                  key={item}
                  href={`#${item.toLowerCase()}`}
                  className={`px-5 py-2.5 rounded-full font-medium transition-all duration-300 relative group ${
                    isScrolled 
                      ? 'text-gray-700 hover:text-blue-600 hover:bg-blue-50' 
                      : 'text-white/90 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <span>{item}</span>
                  <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-0 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500 group-hover:w-3/4 transition-all duration-300"></span>
                </a>
              ))}
            </div>

            {/* Right Side Actions - Enhanced */}
            <div className="flex items-center space-x-4">
              {/* Theme Toggle in Navbar */}
              <button 
                onClick={toggleTheme}
                className={`p-2 rounded-full ${isScrolled ? 'bg-gray-100' : 'bg-white/20'} transition-colors group relative`}
                aria-label="Toggle theme"
              >
                <themeInfo.icon className={`w-5 h-5 ${isScrolled ? 'text-gray-700' : 'text-white'}`} />
                <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                  {themeInfo.label}
                </span>
              </button>
              
              <button className="relative group" onClick={() => showNotification('Search feature coming soon!', 'info')}>
                <Search className={`w-5 h-5 ${isScrolled ? 'text-gray-600' : 'text-white'}`} />
              </button>
              
              <button 
                onClick={() => {
                  if (window.bootstrap && window.bootstrap.Modal) {
                    const modal = new window.bootstrap.Modal(document.getElementById('modalLoginForm'));
                    modal.show();
                  } else {
                    showNotification('Login feature coming soon!', 'info');
                  }
                }}
                className="group relative overflow-hidden bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center space-x-2 btn-magnetic"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-700 to-purple-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <span className="relative z-10 flex items-center space-x-2">
                  <LogIn className="w-5 h-5" />
                  <span>Login</span>
                </span>
              </button>

              <button 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-white/10 transition-colors"
              >
                <Menu className={`w-6 h-6 ${isScrolled ? 'text-gray-700' : 'text-white'}`} />
              </button>
            </div>
          </div>

          {/* Enhanced Mobile Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden fixed inset-0 z-50 bg-white/95 backdrop-blur-md flex flex-col items-center justify-center">
              <button 
                onClick={() => setMobileMenuOpen(false)}
                className="absolute top-6 right-6 p-3 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
              >
                <X className="w-6 h-6 text-gray-700" />
              </button>
              
              <div className="flex flex-col items-center space-y-6 p-8">
                {['Home', 'Properties', 'Invest', 'About', 'Contact'].map((item) => (
                  <a
                    key={item}
                    href={`#${item.toLowerCase()}`}
                    className="text-3xl font-bold text-gray-800 hover:text-blue-600 transition-colors transform hover:scale-110 duration-300"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item}
                  </a>
                ))}
                {/* Theme toggle in mobile menu */}
                <button 
                  onClick={toggleTheme}
                  className="flex items-center space-x-3 text-2xl font-bold text-gray-800 hover:text-blue-600 transition-colors transform hover:scale-110 duration-300"
                >
                  <themeInfo.icon className="w-6 h-6" />
                  <span>{themeInfo.label} Mode</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Enhanced Hero Section with Custom React Carousel */}
      <section className="relative h-screen flex items-center pt-16 overflow-hidden">
        {/* Animated Background Particles */}
        {particles.map(particle => (
          <div
            key={particle.id}
            className="particle"
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              animationDelay: `${particle.delay}s`,
              animationDuration: `${particle.duration}s`
            }}
          />
        ))}
        
        {/* Custom React Carousel */}
        <div className="relative h-full w-full">
          {heroSlides.map((slide, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
              }`}
            >
              <div 
                className="view h-full w-full"
                style={{ 
                  backgroundImage: slide.backgroundImage,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  height: '100vh'
                }}
              >
                <div className="mask rgba-black-light absolute inset-0 bg-black/60"></div>
                <div className="container h-full flex justify-center items-center">
                  <div className="text-center" style={textContainerStyle}>
                    <div className="inline-flex items-center space-x-2 mb-6 animate-pulse">
                      <slide.icon className="w-6 h-6 text-amber-300" />
                      <span className="text-amber-300 font-medium text-lg">{slide.subtitle}</span>
                    </div>

                    <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
                      {slide.title} <span className="bg-gradient-to-r from-amber-300 via-amber-400 to-amber-500 bg-clip-text text-transparent">Excellence</span>
                    </h1>
                    <p className="text-xl md:text-2xl text-gray-200 mb-10 max-w-2xl mx-auto leading-relaxed">
                      {slide.description}
                    </p>
                    
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <button className="group relative overflow-hidden bg-gradient-to-r from-amber-500 to-orange-500 text-white px-8 py-4 rounded-2xl font-bold text-lg shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-3">
                        <span>{slide.buttonText}</span>                       
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
          
          {/* Carousel Controls */}
          <button 
            onClick={prevSlide}
            className="absolute top-1/2 left-4 transform -translate-y-1/2 p-3 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-colors z-20"
          >
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>
          
          <button 
            onClick={nextSlide}
            className="absolute top-1/2 right-4 transform -translate-y-1/2 p-3 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-colors z-20"
          >
            <ChevronRight className="w-6 h-6 text-white" />
          </button>
          
          {/* Carousel Indicators */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2 z-20">
            {heroSlides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 cursor-pointer ${
                  index === currentSlide ? 'bg-white w-8' : 'bg-white/50 hover:bg-white/75'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Play/Pause Button */}
        <button 
          onClick={() => setIsPlaying(!isPlaying)}
          className="absolute bottom-8 right-8 p-3 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-colors z-20"
        >
          {isPlaying ? <Pause className="w-6 h-6 text-white" /> : <Play className="w-6 h-6 text-white" />}
        </button>
      </section>

      {/* Enhanced Stats Section */}
      <div className="relative -mt-24 z-20 mb-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {statsData.map((stat, index) => (
              <div 
                key={index}
                className={`relative overflow-hidden bg-white rounded-3xl shadow-2xl p-8 transform transition-all duration-700 hover:scale-105 hover:shadow-3xl card-3d ${
                  showStats ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                }`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                {/* Animated background */}
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-5`}></div>
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-transparent via-white/10 to-transparent transform rotate-45 -translate-y-16 translate-x-16"></div>
                
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-6">
                    <div className={`p-4 rounded-2xl bg-gradient-to-br ${stat.color} shadow-lg group hover:scale-110 transition-transform duration-300`}>
                      <stat.icon className="w-7 h-7 text-white" />
                    </div>
                    <div className="text-right">
                      <div className="text-4xl font-bold text-gray-900">{stat.value}</div>
                      <div className="text-sm font-medium text-gray-500 mt-1">{stat.label}</div>
                    </div>
                  </div>
                  
                  <div className="h-1 w-full bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 rounded-full mb-4 shimmer"></div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-gray-600 text-sm">
                      <TrendingUp className="w-4 h-4 text-emerald-500 mr-2" />
                      <span>{stat.trend || ''}</span>
                    </div>
                    <div className="text-xs text-gray-500">{stat.description}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Enhanced Properties Section with Filters */}
      <section id="properties" className="py-32 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
          <div className="absolute -top-40 -left-40 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl animate-spin-slow"></div>
          <div className="absolute -bottom-40 -right-40 w-80 h-80 bg-purple-500/5 rounded-full blur-3xl animate-spin-slow" style={{animationDelay: '2s'}}></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-20">
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-100 to-purple-100 rounded-full px-6 py-3 mb-6 animate-pulse">
              <Map className="w-5 h-5 text-blue-600" />
              <span className="text-blue-700 font-semibold">Premium Properties</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Discover Your <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Dream</span> Property
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Explore our exclusive collection of premium properties with advanced filtering and comparison tools.
            </p>
          </div>

          {/* Enhanced Filter Bar */}
          <div className="mb-12 p-6 bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl border border-gray-100">
            <div className="flex flex-col lg:flex-row justify-between items-center gap-6">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search by plot number, size, or price..."
                    className="pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
                    value={searchQuery}
                    onChange={handleSearch}
                  />
                </div>
              </div>
              
              <div className="flex flex-wrap gap-3">
                <div className="flex items-center space-x-2">
                  <Filter className="w-5 h-5 text-gray-500" />
                  <span className="text-gray-700 font-medium">Filter:</span>
                </div>
                
                {['all', 'affordable', 'premium', 'large'].map(filter => (
                  <button
                    key={filter}
                    onClick={() => applyFilter(filter)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                      activeFilter === filter
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {filter.charAt(0).toUpperCase() + filter.slice(1)}
                  </button>
                ))}
              </div>
              
              <div className="flex items-center space-x-4">
                <select 
                  onChange={(e) => handleSort(e.target.value)}
                  className="px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="default">Sort by</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="size">Size: Largest First</option>
                </select>
                
                {compareList.length > 0 && (
                  <button 
                    onClick={() => setShowCompareModal(true)}
                    className="px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-medium flex items-center space-x-2"
                  >
                    <BarChart3 className="w-5 h-5" />
                    <span>Compare ({compareList.length})</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Tabs Navigation - Enhanced */}
          <div className="flex flex-wrap justify-center gap-4 mb-16">
            {[
              { id: 'home', label: 'Overview', icon: Globe, color: 'from-blue-500 to-cyan-500' },
              { id: 'plots', label: 'Ridgehill Estate', icon: Building2, color: 'from-purple-500 to-pink-500' },
              { id: 'houses', label: 'Hardstone Estate', icon: Landmark, color: 'from-emerald-500 to-green-500' },
              { id: 'favorites', label: 'Favorites', icon: Heart, color: 'from-red-500 to-pink-500' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`group flex items-center space-x-3 px-8 py-4 rounded-2xl font-semibold transition-all duration-300 relative overflow-hidden ${
                  activeTab === tab.id 
                    ? `bg-gradient-to-r ${tab.color} text-white shadow-2xl shadow-blue-500/30` 
                    : 'bg-white text-gray-700 hover:bg-gray-50 shadow-lg hover:shadow-xl border border-gray-100'
                }`}
              >
                <div className="absolute inset-0 bg-gradient-to-r ${tab.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                <tab.icon className={`w-5 h-5 ${activeTab === tab.id ? 'text-white' : 'text-gray-500'}`} />
                <span>{tab.label}</span>
                {tab.id === 'favorites' && favorites.length > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center">
                    {favorites.length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Tab Content - Enhanced */}
          <div className="min-h-[600px]">
            {activeTab === 'home' && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {features.map((feature, index) => (
                  <div 
                    key={index}
                    className="group relative overflow-hidden bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 p-8 border border-gray-100 card-3d"
                  >
                    {/* Feature Icon */}
                    <div className={`p-5 rounded-2xl bg-gradient-to-br ${feature.color} w-fit mb-8 transform group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                      <feature.icon className="w-8 h-8 text-white" />
                    </div>

                    <h3 className="text-2xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                    <p className="text-gray-600 mb-6 leading-relaxed">{feature.description}</p>

                    <div className="mb-6">
                      <div className="text-sm text-gray-500 mb-2">Key Features:</div>
                      <ul className="space-y-3">
                        {feature.items.map((item, idx) => (
                          <li key={idx} className="flex items-center text-gray-700 group/item">
                            <div className="p-1 bg-blue-100 rounded-full mr-3 group-hover/item:scale-110 transition-transform">
                              <CheckCircle className="w-4 h-4 text-blue-600" />
                            </div>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'plots' && (
              <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
                <div className="p-8 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-blue-50">
                  <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
                    <div>
                      <h3 className="text-4xl font-bold text-gray-900 mb-2">Ridgehill Estate</h3>
                      <p className="text-gray-600 flex items-center">
                        <MapPin className="w-5 h-5 text-blue-500 mr-2" />
                        Premium residential plots with modern infrastructure and amenities
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="overflow-x-auto">
                  <table id="ridgehillPlots" className="w-full">
                    <thead className="bg-gradient-to-r from-gray-50 to-blue-50">
                      <tr>
                        <th className="py-5 px-8 text-left font-bold text-gray-700 text-sm uppercase tracking-wider">Plot #</th>
                        <th className="py-5 px-8 text-left font-bold text-gray-700 text-sm uppercase tracking-wider">Area (m2)</th>
                        <th className="py-5 px-8 text-left font-bold text-gray-700 text-sm uppercase tracking-wider">Price (ZMW)</th>
                        <th className="py-5 px-8 text-left font-bold text-gray-700 text-sm uppercase tracking-wider">Status</th>
                        <th className="py-5 px-8 text-left font-bold text-gray-700 text-sm uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {plotsRidgehill.map((plot, index) => (
                        <tr key={index} className="hover:bg-blue-50/50 transition-colors duration-200 group">
                          <td className="py-5 px-8">
                            <div className="flex items-center space-x-3">
                              <div className="p-2 bg-blue-100 rounded-lg group-hover:scale-110 transition-transform">
                                <MapPin className="w-5 h-5 text-blue-600" />
                              </div>
                              <div>
                                <div className="font-bold text-gray-900 text-lg group-hover:text-blue-600 transition-colors cursor-pointer" onClick={() => viewPlotDetails(plot)}>
                                  {plot.plotNumberNew}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="py-5 px-8">
                            <span className="px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold inline-flex items-center">
                              <Navigation className="w-4 h-4 mr-2" />
                              {plot.plotAreasNew}
                            </span>
                          </td>
                          <td className="py-5 px-8">
                            <div className="font-bold text-gray-900 text-lg">{plot.plotPricesNew}</div>
                          </td>
                          <td className="py-5 px-8">
                            <span className="px-4 py-2 bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-700 rounded-full text-sm font-bold inline-flex items-center">
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Available
                            </span>
                          </td>
                          <td className="py-5 px-8">
                            <div className="flex items-center space-x-2">
                              <button 
                                onClick={() => toggleFavorite(plot)}
                                className={`p-2 rounded-lg ${favorites.some(f => f.id === plot.id) ? 'text-red-500 bg-red-50' : 'text-gray-500 hover:text-red-500 hover:bg-red-50'}`}
                              >
                                <Heart className={`w-5 h-5 ${favorites.some(f => f.id === plot.id) ? 'fill-current' : ''}`} />
                              </button>
                              <button 
                                onClick={() => toggleCompare(plot)}
                                className={`p-2 rounded-lg ${compareList.some(f => f.id === plot.id) ? 'text-blue-500 bg-blue-50' : 'text-gray-500 hover:text-blue-500 hover:bg-blue-50'}`}
                              >
                                <BarChart3 className="w-5 h-5" />
                              </button>
                              <button 
                                onClick={() => sharePlot(plot)}
                                className="p-2 rounded-lg text-gray-500 hover:text-purple-500 hover:bg-purple-50"
                              >
                                <Share2 className="w-5 h-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'houses' && (
              <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
                <div className="p-8 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-purple-50">
                  <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
                    <div>
                      <h3 className="text-4xl font-bold text-gray-900 mb-2">Hardstone Estate</h3>
                      <p className="text-gray-600 flex items-center">
                        <Landmark className="w-5 h-5 text-purple-500 mr-2" />
                        Luxury plots with premium amenities and comprehensive services
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="overflow-x-auto">
                  <table id="hardstonePlots" className="w-full">
                    <thead className="bg-gradient-to-r from-gray-50 to-purple-50">
                      <tr>
                        <th className="py-5 px-8 text-left font-bold text-gray-700 text-sm uppercase tracking-wider">Plot #</th>
                        <th className="py-5 px-8 text-left font-bold text-gray-700 text-sm uppercase tracking-wider">Area (m2)</th>
                        <th className="py-5 px-8 text-left font-bold text-gray-700 text-sm uppercase tracking-wider">Price (ZMW)</th>
                        <th className="py-5 px-8 text-left font-bold text-gray-700 text-sm uppercase tracking-wider">Status</th>
                        <th className="py-5 px-8 text-left font-bold text-gray-700 text-sm uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {plotsHardstone.map((plot, index) => (
                        <tr key={index} className="hover:bg-purple-50/50 transition-colors duration-200 group">
                          <td className="py-5 px-8">
                            <div className="flex items-center space-x-3">
                              <div className="p-2 bg-purple-100 rounded-lg group-hover:scale-110 transition-transform">
                                <Landmark className="w-5 h-5 text-purple-600" />
                              </div>
                              <div>
                                <div className="font-bold text-gray-900 text-lg group-hover:text-purple-600 transition-colors cursor-pointer" onClick={() => viewPlotDetails(plot)}>
                                  {plot.plotNumberNew}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="py-5 px-8">
                            <span className="px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold inline-flex items-center">
                              <Target className="w-4 h-4 mr-2" />
                              {plot.plotAreasNew}
                            </span>
                          </td>
                          <td className="py-5 px-8">
                            <div className="font-bold text-gray-900 text-lg">{plot.plotPricesNew}</div>
                          </td>
                          <td className="py-5 px-8">
                            <span className="px-4 py-2 bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-700 rounded-full text-sm font-bold inline-flex items-center">
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Available
                            </span>
                          </td>
                          <td className="py-5 px-8">
                            <div className="flex items-center space-x-2">
                              <button 
                                onClick={() => toggleFavorite(plot)}
                                className={`p-2 rounded-lg ${favorites.some(f => f.id === plot.id) ? 'text-red-500 bg-red-50' : 'text-gray-500 hover:text-red-500 hover:bg-red-50'}`}
                              >
                                <Heart className={`w-5 h-5 ${favorites.some(f => f.id === plot.id) ? 'fill-current' : ''}`} />
                              </button>
                              <button 
                                onClick={() => toggleCompare(plot)}
                                className={`p-2 rounded-lg ${compareList.some(f => f.id === plot.id) ? 'text-blue-500 bg-blue-50' : 'text-gray-500 hover:text-blue-500 hover:bg-blue-50'}`}
                              >
                                <BarChart3 className="w-5 h-5" />
                              </button>
                              <button 
                                onClick={() => sharePlot(plot)}
                                className="p-2 rounded-lg text-gray-500 hover:text-purple-500 hover:bg-purple-50"
                              >
                                <Share2 className="w-5 h-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'favorites' && (
              <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
                <div className="p-8 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-red-50">
                  <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
                    <div>
                      <h3 className="text-4xl font-bold text-gray-900 mb-2">Favorite Properties</h3>
                      <p className="text-gray-600 flex items-center">
                        <Heart className="w-5 h-5 text-red-500 mr-2" />
                        Your saved properties for quick access
                      </p>
                    </div>
                    <div className="text-2xl font-bold text-red-600">
                      {favorites.length} Properties
                    </div>
                  </div>
                </div>
                
                {favorites.length > 0 ? (
                  <div className="p-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {favorites.map((plot, index) => (
                        <div key={index} className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-shadow">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center space-x-3">
                              <div className="p-2 bg-red-100 rounded-lg">
                                <MapPin className="w-5 h-5 text-red-600" />
                              </div>
                              <div>
                                <div className="font-bold text-gray-900">{plot.plotNumberNew}</div>
                                <div className="text-gray-500 text-sm">{plot.plotAreasNew}</div>
                              </div>
                            </div>
                            <button 
                              onClick={() => toggleFavorite(plot)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                            >
                              <Heart className="w-5 h-5 fill-current" />
                            </button>
                          </div>
                          <div className="text-lg font-bold text-gray-900 mb-2">{plot.plotPricesNew}</div>
                          <div className="flex items-center justify-between mt-4">
                            <button 
                              onClick={() => viewPlotDetails(plot)}
                              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium"
                            >
                              View Details
                            </button>
                            <button 
                              onClick={() => toggleCompare(plot)}
                              className={`px-4 py-2 rounded-lg text-sm font-medium ${compareList.some(f => f.id === plot.id) ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}
                            >
                              Compare
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="p-12 text-center">
                    <Heart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h4 className="text-xl font-semibold text-gray-700 mb-2">No favorites yet</h4>
                    <p className="text-gray-500 mb-6">Start adding properties to your favorites list</p>
                    <button 
                      onClick={() => setActiveTab('plots')}
                      className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium"
                    >
                      Browse Properties
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Enhanced Interactive Maps Section */}
      <section className="py-32 bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              radial-gradient(circle at 20% 80%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)
            `,
            animation: 'gradientPulse 8s ease-in-out infinite'
          }}></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-500/20 to-purple-500/20 backdrop-blur-sm rounded-full px-6 py-3 mb-6 border border-white/10">
              <Map className="w-5 h-5 text-blue-300" />
              <span className="text-blue-200 font-semibold">Interactive 3D Maps</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Explore With <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Precision</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Navigate through our estates with detailed interactive maps. Click on any plot for comprehensive information.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Enhanced Ridgehill Map */}
            <div className="group relative overflow-hidden bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl shadow-2xl border border-gray-700/50 hover:border-blue-500/50 transition-all duration-500 card-3d">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-cyan-500 to-blue-500 shimmer"></div>
              <div className="p-8 bg-gradient-to-r from-blue-600/20 to-blue-500/10">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-2">Ridgehill Estate 3D Map</h3>
                    <p className="text-blue-200 flex items-center">
                      <MapPin className="w-5 h-5 mr-2" />
                      Interactive visualization with real-time data
                    </p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <button className="p-2 bg-blue-500/20 rounded-xl hover:bg-blue-500/30 transition-colors">
                      <ZoomIn className="w-5 h-5 text-blue-300" />
                    </button>
                    <button className="p-2 bg-blue-500/20 rounded-xl hover:bg-blue-500/30 transition-colors">
                      <Layers className="w-5 h-5 text-blue-300" />
                    </button>
                    <div className="p-3 bg-blue-500/20 rounded-xl group-hover:rotate-180 transition-transform duration-500">
                      <Navigation className="w-6 h-6 text-blue-300" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="h-[500px] relative">
                <PlotMap />
                {/* Map Controls Overlay */}
                <div className="absolute bottom-4 left-4 flex space-x-2">
                  <button className="p-2 bg-white/10 backdrop-blur-sm rounded-lg hover:bg-white/20 transition-colors">
                    <Compass className="w-5 h-5 text-white" />
                  </button>
                  <button className="p-2 bg-white/10 backdrop-blur-sm rounded-lg hover:bg-white/20 transition-colors">
                    <Target className="w-5 h-5 text-white" />
                  </button>
                </div>
              </div>
            </div>

            {/* Enhanced Hardstone Map */}
            <div className="group relative overflow-hidden bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl shadow-2xl border border-gray-700/50 hover:border-purple-500/50 transition-all duration-500 card-3d">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500 shimmer"></div>
              <div className="p-8 bg-gradient-to-r from-purple-600/20 to-pink-500/10">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-2">Hardstone Estate 3D Map</h3>
                    <p className="text-purple-200 flex items-center">
                      <Landmark className="w-5 h-5 mr-2" />
                      Premium plots with satellite imagery
                    </p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <button className="p-2 bg-purple-500/20 rounded-xl hover:bg-purple-500/30 transition-colors">
                      <Maximize2 className="w-5 h-5 text-purple-300" />
                    </button>
                    <button className="p-2 bg-purple-500/20 rounded-xl hover:bg-purple-500/30 transition-colors">
                      <RefreshCw className="w-5 h-5 text-purple-300" />
                    </button>
                    <div className="p-3 bg-purple-500/20 rounded-xl group-hover:rotate-180 transition-transform duration-500">
                      <MapPinned className="w-6 h-6 text-purple-300" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="h-[500px] relative">
                <PlotMapHardstone />
                {/* Map Stats Overlay */}
                <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-sm rounded-lg p-3">
                  <div className="text-white text-sm">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span>{plotsHardstone.length} Plots Available</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Footer */}
      <footer className="bg-gradient-to-b from-gray-900 to-gray-950 text-white py-20 relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-blue-500/5 via-transparent to-purple-500/5"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
            {/* Enhanced Company Info */}
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-3 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl animate-pulse">
                  <Home className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">Hardstone<span className="text-blue-400">Properties</span></h2>
                  <p className="text-gray-400 text-sm">Premium Real Estate Since 2008</p>
                </div>
              </div>
              <p className="text-gray-400 mb-6 leading-relaxed">
                Transforming real estate experiences with premium properties, exceptional service, and innovative solutions.
              </p>
              <div className="flex space-x-3">
                {[
                  { icon: Facebook, color: 'hover:bg-blue-600', label: 'Facebook' },
                  { icon: Instagram, color: 'hover:bg-pink-600', label: 'Instagram' },
                  { icon: Twitter, color: 'hover:bg-blue-400', label: 'Twitter' },
                  { icon: MessageCircle, color: 'hover:bg-green-500', label: 'WhatsApp' }
                ].map((social, index) => (
                  <a 
                    key={index}
                    href="#" 
                    className={`p-3 bg-gray-800 rounded-xl ${social.color} transition-all duration-300 transform hover:scale-110 group relative`}
                    aria-label={social.label}
                  >
                    <social.icon className="w-5 h-5" />
                    <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                      {social.label}
                    </span>
                  </a>
                ))}
              </div>
            </div>

            {/* Quick Links with Icons */}
            <div>
              <h3 className="text-lg font-bold mb-6 text-white flex items-center">
                <Navigation className="w-5 h-5 mr-2 text-blue-400" />
                Quick Links
              </h3>
              <ul className="space-y-3">
                {['Home', 'Properties', 'About Us', 'Contact', 'Investment Guide', 'FAQ'].map((link, index) => (
                  <li key={link}>
                    <a href="#" className="text-gray-400 hover:text-white transition-colors flex items-center space-x-2 group">
                      <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                      <span>{link}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Our Estates with Status */}
            <div>
              <h3 className="text-lg font-bold mb-6 text-white flex items-center">
                <Building2 className="w-5 h-5 mr-2 text-purple-400" />
                Our Estates
              </h3>
              <ul className="space-y-3">
                {[
                  { name: 'Ridgehill Estate', plots: plotsRidgehill.length, icon: Building2 },
                  { name: 'Hardstone Estate', plots: plotsHardstone.length, icon: Landmark },
                  { name: 'New Developments', plots: 'Coming Soon', icon: Zap },
                  { name: 'Commercial Spaces', plots: '25+', icon: Briefcase }
                ].map((estate) => (
                  <li key={estate.name}>
                    <a href="#" className="text-gray-400 hover:text-white transition-colors flex items-center justify-between group">
                      <div className="flex items-center space-x-2">
                        <estate.icon className="w-4 h-4" />
                        <span>{estate.name}</span>
                      </div>
                      <span className="text-xs bg-gray-800 px-2 py-1 rounded">{estate.plots} plots</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Enhanced Contact Info */}
            <div>
              <h3 className="text-lg font-bold mb-6 text-white flex items-center">
                <Phone className="w-5 h-5 mr-2 text-green-400" />
                Contact Info
              </h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3 group hover:bg-white/5 p-3 rounded-xl transition-colors">
                  <Phone className="w-5 h-5 text-blue-400 mt-1 group-hover:scale-110 transition-transform" />
                  <div>
                    <p className="font-medium">Phone</p>
                    <p className="text-gray-400 group-hover:text-white transition-colors">+260 123 456 789</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 group hover:bg-white/5 p-3 rounded-xl transition-colors">
                  <Mail className="w-5 h-5 text-blue-400 mt-1 group-hover:scale-110 transition-transform" />
                  <div>
                    <p className="font-medium">Email</p>
                    <p className="text-gray-400 group-hover:text-white transition-colors">info@hardstone.co.zm</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 group hover:bg-white/5 p-3 rounded-xl transition-colors">
                  <Clock className="w-5 h-5 text-blue-400 mt-1 group-hover:scale-110 transition-transform" />
                  <div>
                    <p className="font-medium">Business Hours</p>
                    <p className="text-gray-400 group-hover:text-white transition-colors">Mon - Fri: 8AM - 6PM</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-500">
                &copy; {new Date().getFullYear()} Hardstone Properties. All rights reserved.
              </p>
              <div className="flex items-center space-x-6 mt-4 md:mt-0">
                <a href="#" className="text-gray-500 hover:text-white transition-colors">Privacy Policy</a>
                <a href="#" className="text-gray-500 hover:text-white transition-colors">Terms of Service</a>
                <a href="#" className="text-gray-500 hover:text-white transition-colors">Cookie Policy</a>
                <div className="flex items-center">
                  <Heart className="w-4 h-4 text-red-500 mx-1" />
                  <span className="text-gray-500">Made By <a href="https://www.kanchtech.com" className="text-blue-400 hover:text-blue-300">Kanch Innovative Technologies</a></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Enhanced Login Modal */}
      <div className="modal fade" id="modalLoginForm" tabIndex="-1" aria-labelledby="modalLoginFormLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered max-w-md">
          <div className="modal-content overflow-hidden rounded-3xl border-0 shadow-2xl">
            {/* Modal Header with Gradient */}
            <div className="modal-header border-0 p-0">
              <div className="relative w-full">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-500 animate-gradientPulse"></div>
                <div className="relative p-8 text-center">
                  <div className="flex justify-between items-start mb-6">
                    <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-sm transform hover:rotate-12 transition-transform">
                      <Lock className="w-8 h-8 text-white" />
                    </div>
                    <button 
                      type="button" 
                      className="btn-close btn-close-white opacity-100 hover:opacity-75 transition-opacity p-2 bg-white/20 rounded-full"
                      data-bs-dismiss="modal" 
                      aria-label="Close"
                    >
                      <X className="w-5 h-5 text-white" />
                    </button>
                  </div>
                  <h5 className="modal-title text-white text-3xl font-bold mb-2" id="modalLoginFormLabel">
                    LOGIN
                  </h5>
                  <p className="text-blue-100 text-lg">
                    Sign in to access premium features
                  </p>
                </div>
              </div>
            </div>

            {/* Modal Body */}
            <div className="modal-body p-8 bg-white">
              {loading && (
                <div className="flex flex-col items-center justify-center mb-6 py-4">
                  <div className="loading-dots">
                    <div></div><div></div><div></div><div></div>
                  </div>
                  <p className="text-gray-600 font-medium mt-4">Authenticating...</p>
                </div>
              )}
              
              <form onSubmit={handleLogin} className="space-y-6">
                {/* Username Field */}
                <div>
                  <label className="block text-gray-700 mb-2 font-semibold text-sm uppercase tracking-wider">
                    <Mail className="inline w-4 h-4 mr-2 text-gray-400" />
                    Username or Email
                  </label>
                  <div className="relative group">
                    <User className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 group-focus-within:text-blue-500 transition-colors" />
                    <input 
                      type="text" 
                      className="w-full pl-12 pr-4 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all group-hover:border-blue-300"
                      value={username}
                      onChange={e => setUsername(e.target.value)}
                      placeholder="Enter your username or email"
                    />
                  </div>
                </div>

                {/* Password Field */}
                <div>
                  <label className="block text-gray-700 mb-2 font-semibold text-sm uppercase tracking-wider">
                    <Lock className="inline w-4 h-4 mr-2 text-gray-400" />
                    Password
                  </label>
                  <div className="relative group">
                    <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 group-focus-within:text-blue-500 transition-colors" />
                    <input 
                      type={showPassword ? "text" : "password"} 
                      className="w-full pl-12 pr-12 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all group-hover:border-blue-300"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Enter your password"
                    />
                    <button 
                      type="button" 
                      onClick={togglePasswordVisibility}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Remember & Forgot */}
                <div className="flex items-center justify-between">
                  <label className="flex items-center space-x-2 cursor-pointer group">
                    <div className="relative">
                      <input type="checkbox" className="sr-only" />
                      <div className="w-5 h-5 border border-gray-300 rounded group-hover:border-blue-500 transition-colors flex items-center justify-center">
                        <CheckCircle className="w-4 h-4 text-blue-600 opacity-0 group-has-checked:opacity-100 transition-opacity" />
                      </div>
                    </div>
                    <span className="text-gray-600 text-sm">Remember me</span>
                  </label>
                  <Link 
                    to="/ForgotPassword" 
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors"
                    data-bs-dismiss="modal"
                  >
                    Forgot password?
                  </Link>
                </div>

                {/* Submit Button */}
                <button 
                  type="submit" 
                  disabled={!username || !password || loading}
                  className={`w-full py-4 rounded-xl font-bold text-lg transition-all duration-300 relative overflow-hidden ${
                    username && password && !loading
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-xl hover:scale-105' 
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-700 to-purple-700 opacity-0 hover:opacity-100 transition-opacity"></div>
                  {loading ? (
                    <span className="relative z-10 flex items-center justify-center space-x-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Signing In...</span>
                    </span>
                  ) : (
                    <span className="relative z-10 flex items-center justify-center space-x-2">
                      <LogIn className="w-5 h-5" />
                      <span>Sign In</span>
                    </span>
                  )}
                </button>

                {/* Divider */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-gray-500">Or continue with</span>
                  </div>
                </div>

                {/* Social Login */}
                <div className="grid grid-cols-1 gap-3">
                  <button 
                    type="button"
                    className="flex items-center justify-center space-x-2 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors group"
                  >
                    <Mail className="w-5 h-5 text-red-600 group-hover:scale-110 transition-transform" />
                    <span className="text-gray-700 font-medium">Google</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Plot Details Modal */}
      {showPlotModal && selectedPlot && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="p-8 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-purple-50">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-3xl font-bold text-gray-900">{selectedPlot.plotNumberNew}</h3>
                  <p className="text-gray-600 mt-2">Premium Residential Plot</p>
                </div>
                <button 
                  onClick={() => setShowPlotModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-6 h-6 text-gray-500" />
                </button>
              </div>
            </div>
            
            {/* Modal Body */}
            <div className="p-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left Column - Images */}
                <div>
                  <div className="aspect-video bg-gray-100 rounded-2xl mb-4"></div>
                  <div className="grid grid-cols-3 gap-2">
                    {[1, 2, 3].map((img) => (
                      <div key={img} className="aspect-square bg-gray-100 rounded-lg"></div>
                    ))}
                  </div>
                </div>
                
                {/* Right Column - Details */}
                <div>
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">Plot Details</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-gray-600 text-sm">Size</p>
                          <p className="font-semibold">{selectedPlot.plotAreasNew}</p>
                        </div>
                        <div>
                          <p className="text-gray-600 text-sm">Price</p>
                          <p className="font-semibold">{selectedPlot.plotPricesNew}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">Mortgage Calculator</h4>
                      <div className="bg-gray-50 rounded-xl p-4">
                        <div className="grid grid-cols-3 gap-4 text-center">
                          <div>
                            <p className="text-gray-600 text-sm">Monthly</p>
                            <p className="font-bold text-lg text-blue-600">K{selectedPlot.calculatedMortgage?.monthly}</p>
                          </div>
                          <div>
                            <p className="text-gray-600 text-sm">Down Payment</p>
                            <p className="font-bold text-lg text-purple-600">K{selectedPlot.calculatedMortgage?.downPayment}</p>
                          </div>
                          <div>
                            <p className="text-gray-600 text-sm">Total</p>
                            <p className="font-bold text-lg text-emerald-600">K{selectedPlot.calculatedMortgage?.total}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">Amenities</h4>
                      <div className="flex flex-wrap gap-2">
                        {['Water', 'Electricity', 'Road', 'Security', 'Park'].map(amenity => (
                          <span key={amenity} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                            {amenity}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="mt-8 pt-8 border-t border-gray-100 flex justify-end space-x-4">
                <button className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium">
                  Save for Later
                </button>
                <button className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-medium">
                  Schedule Visit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Comparison Modal */}
      {showCompareModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex justify-between items-center mb-8">
                <h3 className="text-3xl font-bold text-gray-900">Compare Properties</h3>
                <button 
                  onClick={() => setShowCompareModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-6 h-6 text-gray-500" />
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {compareList.map((plot, index) => (
                  <div key={index} className="border border-gray-200 rounded-2xl p-6">
                    <h4 className="font-bold text-gray-900 mb-4">{plot.plotNumberNew}</h4>
                    <div className="space-y-3">
                      <div>
                        <p className="text-gray-600 text-sm">Price</p>
                        <p className="font-semibold">{plot.plotPricesNew}</p>
                      </div>
                      <div>
                        <p className="text-gray-600 text-sm">Size</p>
                        <p className="font-semibold">{plot.plotAreasNew}</p>
                      </div>
                    </div>
                    <button 
                      onClick={() => setCompareList(compareList.filter(p => p.id !== plot.id))}
                      className="mt-4 text-red-600 text-sm font-medium"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
              
              {compareList.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-gray-600">No properties selected for comparison</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Virtual Tour Modal */}
      {showVirtualTour && (
        <div className="fixed inset-0 z-[9999] bg-black">
          <div className="relative w-full h-full">
            <button 
              onClick={() => setShowVirtualTour(false)}
              className="absolute top-4 right-4 z-10 p-3 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-colors"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-white">
                <div className="text-4xl font-bold mb-4">Virtual Tour</div>
                <p className="text-xl mb-8">Coming Soon - Advanced 3D Visualization</p>
                <button 
                  onClick={() => setShowVirtualTour(false)}
                  className="px-6 py-3 bg-white text-black rounded-xl font-medium"
                >
                  Exit Tour
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Audio Player (Optional Background Music) */}
      <audio ref={audioRef} loop>
        <source src="https://assets.mixkit.co/music/preview/mixkit-tech-house-vibes-130.mp3" type="audio/mpeg" />
      </audio>
    </main>
  );
};

export default HomePage;